import { useRegistry } from '@/hooks/useRegistry'
import { fetch } from '@/lib/index/fetch-plannings-twirp'
import type { DefaultValueOption } from '@ttab/elephant-ui'
import { Button, Checkbox, ComboBox, Input, Label } from '@ttab/elephant-ui'
import {
  ArrowRightLeftIcon,
  BriefcaseBusinessIcon,
  CalendarDaysIcon,
  CalendarIcon,
  CircleXIcon,
  GanttChartSquareIcon
} from '@ttab/elephant-ui/icons'
import { useSession } from 'next-auth/react'
import { useMemo, useState, type JSX } from 'react'
import type { Doc } from 'yjs'
import { View, ViewHeader } from '../View'
import type { ViewProps } from '@/types/index'
import { Form } from '../Form'
import { UserMessage } from '../UserMessage'
import { MovePrompt } from './Prompt'
import type * as Y from 'yjs'
import { deleteByYPath, getValueByYPath, setValueByYPath, toYStructure } from '@/shared/yUtils'
import { Block } from '@ttab/elephant-api/newsdoc'
import { toast } from 'sonner'
import { ToastAction } from '@/components/ToastAction'
import { createPayload } from '@/shared/templates/lib/createPayload'
import { DatePicker } from '../Datepicker'
import { currentDateInUTC, parseDate } from '@/shared/datetime'
import type { YDocument } from '@/modules/yjs/hooks'
import { useTranslation } from 'react-i18next'

export const Move = ({ ydoc, ...props }: ViewProps & {
  ydoc: YDocument<Y.Map<unknown>>
  original: {
    document: Doc | undefined
    assignmentId: string | undefined
    assignmentTitle: string | undefined
    assignment: Y.Map<unknown> | undefined
    planningId: string | undefined
  }
}): JSX.Element => {
  const [selectedPlanning, setSelectedPlanning] = useState<DefaultValueOption | undefined>(undefined)
  const [showVerifyDialog, setShowVerifyDialog] = useState(false)
  const [planningDateString, setPlanningDateString] = useState<string | undefined>()
  const [searchOlder, setSearchOlder] = useState(false)
  const { t } = useTranslation()

  const { data: session } = useSession()
  const { index, locale, timeZone } = useRegistry()


  const date = useMemo(() =>
    planningDateString ? parseDate(planningDateString) || new Date() : new Date(),
  [planningDateString])

  const handleSelectPlanning = (option: DefaultValueOption): void => {
    if (option.value !== selectedPlanning?.value) {
      setSelectedPlanning({ value: option.value, label: option.label })
    } else {
      setSelectedPlanning(undefined)
    }
  }

  const handleSubmit = (): void => {
    setShowVerifyDialog(true)
  }

  const handleClearPlanning = (e: React.MouseEvent): void => {
    e.preventDefault()
    setSelectedPlanning(undefined)
  }

  const payload = useMemo(() => {
    if (props.original.document) {
      return createPayload(props.original.document)
    }
  }, [props.original.document])

  const handleMoveAssignment = (planning: Y.Doc | undefined): void => {
    if (!props.original?.document || !planning) {
      console.error('Missing required data for moving assignment', {
        hasDocument: !!props.original?.document,
        hasPlanning: !!planning
      })
      return
    }

    try {
      // Get original assignment
      const yRootOriginal = props.original.document.getMap('ele')
      const originalAssignmentArray = getValueByYPath<Block[] | undefined>(yRootOriginal, 'meta.core/assignment')?.[0] || []
      const originalAssignmentIndex = originalAssignmentArray.findIndex(
        (assignment: Block) => assignment.id === props.original.assignmentId
      )

      if (originalAssignmentIndex < 0) {
        toast.error(t('errors:toasts.assignmentMoveError'))
        return
      }

      const originalAssignment = getValueByYPath<Block>(yRootOriginal, `meta.core/assignment[${originalAssignmentIndex}]`)?.[0]
      if (!originalAssignment) {
        toast.error(t('errors:toasts.assignmentMoveError'))
        return
      }

      // Prepare new planning for assignment
      const newEle = planning.getMap('ele')
      const selectedPlanningDate = getValueByYPath<string>(newEle, 'meta.core/planning-item[0].data.start_date')?.[0]
      const newDateString = selectedPlanning ? selectedPlanningDate : planningDateString

      const updatedAssignment = {
        ...originalAssignment,
        data: {
          ...originalAssignment.data,
          start_date: newDateString || originalAssignment.data.start_date,
          end_date: newDateString || originalAssignment.data.end_date,
          start: (newDateString && originalAssignment.data.start?.replace(/\d{4}-\d{2}-\d{2}/, newDateString)) || originalAssignment.data.start
        }
      }

      const newAssignmentArray = getValueByYPath<Block[] | undefined>(newEle, 'meta.core/assignment')?.[0] || []
      const newAssignmentIndex = newAssignmentArray.length

      if (!setValueByYPath(newEle, `meta.core/assignment[${newAssignmentIndex}]`, toYStructure(updatedAssignment))) {
        toast.error(t('errors:toasts.assignmentMoveError'))
        return
      }


      // Remove from old planning
      deleteByYPath(yRootOriginal, `meta.core/assignment[${originalAssignmentIndex}]`)

      const newPlanningUUID = getValueByYPath<string>(newEle, 'root.uuid')?.[0]
      toast.success(t('planning:move.assignmentMoveSuccess'), {
        classNames: {
          title: 'whitespace-nowrap'
        },
        action: (
          <ToastAction
            documentId={newPlanningUUID}
            withView='Planning'
            label={t('planning:actions.openPlanning')}
            Icon={CalendarDaysIcon}
          />
        )
      })

      setShowVerifyDialog(false)
      props.onDialogClose?.()
    } catch (error) {
      console.error('Error moving assignment', error)
      toast.error(t('errors:toasts.assignmentMoveError'))
    }
  }

  return (
    <View.Root asDialog={props.asDialog} className={props.className}>
      <ViewHeader.Root>
        <ViewHeader.Content>
          {props.asDialog && (
            <div className='flex w-full h-full items-center space-x-2 font-bold'>
              <ViewHeader.Title
                name='Assignment'
                title={t('planning:move.moveAssignment')}
                icon={ArrowRightLeftIcon}
                iconColor='#006bb3'
                asDialog={props.asDialog}
              />
            </div>
          )}
        </ViewHeader.Content>

        <ViewHeader.Action onDialogClose={props.onDialogClose} asDialog={props.asDialog}>
          {!props.asDialog && !!ydoc.id
            && <ViewHeader.RemoteUsers ydoc={ydoc} />}
        </ViewHeader.Action>
      </ViewHeader.Root>

      <View.Content>
        <Form.Root asDialog={props.asDialog}>
          <Form.Content>
            <Form.Group icon={BriefcaseBusinessIcon}>
              <>
                <Input
                  className='pl-0 pt-2 h-8 text-medium border-0 truncate'
                  readOnly
                  value={props.original.assignmentTitle}
                />
              </>
            </Form.Group>
            <Form.Group icon={GanttChartSquareIcon}>
              <ComboBox
                max={1}
                size='xs'
                className='min-w-0 w-full truncate justify-start max-w-48'
                selectedOptions={selectedPlanning ? [selectedPlanning] : []}
                placeholder={t('planning:move.pickPlanning')}
                modal={props.asDialog}
                fetch={(query) => fetch(query, session, t, index, locale, timeZone, {
                  searchOlder
                })
                  .then((data) =>
                    data.filter((item) => item.value !== props.original.planningId)
                  )}
                minSearchChars={2}
                onSelect={handleSelectPlanning}
                translationStrings={{
                  nothingFound: t('common:misc.nothingFound'),
                  searching: t('common:misc.searching')
                }}
              >
              </ComboBox>

              {!!selectedPlanning
                && (
                  <>
                    <Button
                      variant='ghost'
                      className='text-muted-foreground flex size-4 p-0 hover:bg-accent2'
                      onClick={handleClearPlanning}
                      render={<CircleXIcon size={18} strokeWidth={1.75} />}
                    />
                  </>
                )}
              <>
                <Checkbox
                  id='SearchOlder'
                  defaultChecked={searchOlder}
                  onCheckedChange={(checked: boolean) => { setSearchOlder(checked) }}
                />
                <Label htmlFor='SearchOlder' className='text-muted-foreground'>{t('planning:move.showOlder')}</Label>
              </>
            </Form.Group>
            <UserMessage asDialog={!!props?.asDialog}>
              {!selectedPlanning
                ? (
                    <div className='flex flex-col gap-4'>
                      {t('planning:move.assignmentCreateInfo')}
                      <Form.Group icon={CalendarIcon}>
                        <DatePicker date={date} setDate={(value) => setPlanningDateString(value)} />
                      </Form.Group>
                    </div>
                  )
                : (
                    <>{t('planning:move.assignmentAddInfo')}</>
                  )}
            </UserMessage>
          </Form.Content>

          {
            showVerifyDialog
            && (
              <MovePrompt
                title={t('planning:move.moveAssignment')}
                description={t('planning:move.moveAssignmentConfirm')}
                selectedPlanning={selectedPlanning}
                payload={{
                  ...payload,
                  meta: {
                    ...payload?.meta,
                    'core/planning-item': [
                      Block.create({
                        type: 'core/planning-item',
                        data: {
                          public: 'true',
                          tentative: 'false',
                          start_date: planningDateString || currentDateInUTC(),
                          end_date: planningDateString || currentDateInUTC()
                        }
                      })
                    ]
                  }
                }}
                onPrimary={handleMoveAssignment}
                onSecondary={() => {
                  setShowVerifyDialog(false)
                }}
                primaryLabel={t('planning:move.label')}
                secondaryLabel={t('common:actions.abort')}
              />
            )
          }
          <Form.Footer>
            <Form.Submit
              onSubmit={handleSubmit}
              onReset={() => props.onDialogClose?.()}
            >
              <div className='flex gap-2 justify-end'>
                <Button type='reset'>{t('common:actions.abort')}</Button>
                <Button type='submit'>{t('planning:move.moveAssignment')}</Button>
              </div>
            </Form.Submit>
          </Form.Footer>
        </Form.Root>
      </View.Content>
    </View.Root>
  )
}
